import React from 'react';

import { Button } from "@/components/ui/button";
import { AppUser, ReviewUI } from "@/types/supabase";
import ContactFormDialog from "@/components/ContactFormDialog";
import AdUnit from "@/components/ads/AdUnit";
import AfterIdle from "@/components/util/AfterIdle";
import { Flag, Star } from "lucide-react";

interface ReviewItemProps {
  review: ReviewUI;
  user: AppUser | null;
  isAdmin: boolean;
  showAd: boolean;
  isPremiumProvider: boolean;
  adsEnabled: boolean;
}

const ReviewItem = ({ review, user, isAdmin, showAd, isPremiumProvider, adsEnabled }: ReviewItemProps) => {
  return (
    <React.Fragment>
      <article className="py-6 first:pt-0 last:pb-0">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-heading font-semibold text-brand-ink">
              {user && user.id === review.userId ? "You" : review.userName}
            </h3>
            <div className="flex items-center mt-1">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < review.rating
                        ? "fill-amber text-amber"
                        : "text-brand-cloud"
                    }`}
                  />
                ))}
              </div>
              <span className="ml-2 text-xs text-brand-slate">{review.date}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <span
                className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                  review.isPublic
                    ? "border-brand-cloud bg-brand-mist text-brand-ink"
                    : "border-red-100 bg-red-50 text-red-700"
                }`}
              >
                {review.isPublic ? "Public" : "Hidden"}
              </span>
            )}
            {review.isContactedProvider && (
              <span className="rounded-md border border-brand-cloud bg-brand-mist px-2.5 py-1 text-xs font-medium text-brand-ink">
                Contacted provider
              </span>
            )}
          </div>
        </div>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="leading-7 text-brand-slate">{review.comment || "Great service!"}</p>
          {user && review.userId !== user.id && (
            <div className="shrink-0">
              <ContactFormDialog
                trigger={
                  <Button variant="ghost" size="sm" className="h-8 text-xs text-brand-slate hover:bg-brand-mist hover:text-brand-ink">
                    <Flag className="mr-1 h-3 w-3" />
                    Report
                  </Button>
                }
                reviewDetails={`Reporting review by ${review.userName} posted on ${review.date}:\n"${review.comment}"`}
                reviewId={review.id}
              />
            </div>
          )}
        </div>
      </article>

      {showAd && adsEnabled && !isPremiumProvider && (
        <AfterIdle timeoutMs={1200}>
          <div className="my-6 w-full rounded-md border border-brand-cloud bg-brand-mist p-4">
            <div className="flex justify-center">
              <AdUnit adSlot="1714990002" adFormat="LEADERBOARD" className="w-full" />
            </div>
          </div>
        </AfterIdle>
      )}
    </React.Fragment>
  );
};

export default ReviewItem;
